<template>
    <div>
        <div class="stepper-wrapper">
            <div class="stepper-container">
                <div class="stepper">
                    <div class="stepper-line-background"></div>
                    <div class="stepper-line-progress" :style="{ width: progressWidth }"></div>

                    <div v-for="(step, index) in steps" :key="index" :id="'step-' + (index + 1)" class="step">
                        <div class="circle" :class="{ active: index + 1 <= currentStep, pulsing: index + 1 === currentStep }">
                            <span class="number-circle">{{ index + 1 }}</span>
                        </div>

                        <span class="step-label" :class="{ active: index <= currentStep - 1 }">
                            {{ step.label }}
                        </span>
                    </div>
                </div>
            </div>
        </div>
        <div>
            <transition
                mode="out-in"
                :name="transitionName"
                @after-enter="scrollToStep"
            >
                <section :key="currentStep" :id="'step' + currentStep" :ref="'step' + currentStep">
                    <slot :name="currentComponentSlotName" :changeStep="changeStep" :containerRef="containerRef"></slot>
                </section>
            </transition> 
        </div>
    </div>
</template>

<script>
    export default {
        props: {
            steps: {
                type: Array,
                required: true
            }
        },
        data() {
            return {
                currentStep: 1,
                containerRef: null,
                transitionDirection: "forward"
            }
        },
        computed: {
            progressWidth() {
                let maxWidth = 92;
                let minWidth = 0;

                if (this.currentStep === this.steps.length + 1) {
                    return `${maxWidth}%`;
                } else {
                    if (this.steps.length > 5) {
                        minWidth = 6;
                    } else if (this.steps.length >= 3) {
                        minWidth = 10;
                        maxWidth = 88;
                    } else {
                        minWidth = 24;
                        maxWidth = 76;
                    }
                    
                    const availableRange = maxWidth - minWidth;
                    const progress = ((this.currentStep - 1) / (this.steps.length - 1)) * availableRange + minWidth;
                    return `${progress}%`;
                }
            },
            currentComponentSlotName() {
                return this.steps[this.currentStep - 1].slot;
            },
            transitionName() {
                return this.transitionDirection === "forward" ? "slide-left" : "slide-right";
            },
        },
        methods: {
            scrollToStep() {
                this.$nextTick(() => {
                    const stepElement = document.querySelector(`#step-${this.currentStep}`);
                    if (stepElement) {
                        this.$scrollTo(stepElement, 100);
                    }
                });
            },
            changeStep(back = false, reset = false) {
                if (reset) {
                    this.transitionDirection = 'backward';
                    this.currentStep = 1;
                    this.$nextTick(() => {
                        this.containerRef = this.$refs['step' + this.currentStep];
                        this.scrollToStep();
                    });
                    return;
                }

                this.transitionDirection = back ? 'backward' : 'forward';
                this.currentStep += back ? -1 : 1;
            },
            resetStepper() {
                this.transitionDirection = 'backward'; 
                this.currentStep = 1;
                this.$nextTick(() => {
                    this.containerRef = this.$refs['step' + this.currentStep];
                    this.scrollToStep();
                });
            }
        },
        mounted(){
            const refName = 'step' + this.currentStep;
            const sectionEl = this.$refs[refName];
            this.containerRef = sectionEl;
        }
    };
</script>

<style scoped>
.stepper-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin: auto;
  text-align: center;
  font-family: "Oswald", sans-serif;
  position: relative;
}

.stepper {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  max-width: 1200px;
  position: relative;
  margin-bottom: 30px;
  flex-wrap: nowrap;
}

.stepper-line-background {
  position: absolute;
  top: 18px;
  left: 0;
  right: 0;
  height: 4px;
  background-color: #ddd;
  z-index: 0;
  border-radius: 2px;
}

.stepper-line-progress {
  position: absolute;
  top: 18px;
  left: 0;
  height: 4px;
  background-color: var(--altcolor);
  transition: width 0.5s ease-in-out;
  z-index: 1;
  border-radius: 2px;
  width: 0%;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
  flex: 1;
  min-width: 0;
  text-align: center;
}

.circle {
  width: clamp(26px, 5vw, 40px);
  height: clamp(26px, 5vw, 40px);
  background-color: white;
  border: 3px solid #ccc;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: clamp(12px, 3vw, 20px);
  transition: all 0.3s ease-in-out;
  z-index: 3;
}

.number-circle {
  font-size: 20px;
} 

.circle.active {
  background-color: #151618;
  border-color: #151618;
  color: white;
  font-size: clamp(14px, 3.5vw, 22px);
}

.circle.pulsing::after {
  content: "";
  position: absolute;
  width: clamp(35px, 6vw, 50px);
  height: clamp(35px, 6vw, 50px);
  border-radius: 50%;
  border: 2px solid var(--altcolor);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 0.7;
  }
  100% {
    transform: scale(1.2);
    opacity: 0;
  }
}

.step-label {
  margin-top: 8px;
  font-size: clamp(9px, 2.3vw, 14px);
  color: #aaa;
  transition: color 0.3s ease-in-out;
  text-align: center;
  word-wrap: break-word;
  max-width: 90%;
  white-space: normal;
  line-height: 1.2em;
}

.step-label.active {
  color: #444444;
  font-weight: bold;
}

.step-title {
  font-size: 22px;
  font-weight: bold;
  margin-top: 20px;
}

.step-description {
  font-size: 14px;
  color: #777;
  margin-bottom: 20px;
}

.stepper-buttons {
  margin-top: 15px;
}

.stepper-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 0.3s cubic-bezier(0.3, 0.7, 0.4, 1), opacity 0.25s ease-in-out;
}

.slide-left-enter,
.slide-right-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.slide-left-leave-to,
.slide-right-enter {
  transform: translateX(-100%);
  opacity: 0;
}

@media (max-width: 750px) {

  .stepper-line-background,
  .stepper-line-progress {
    top: 16px;
  }
}

@media (max-width: 600px) {
  .stepper {
    justify-content: space-between;
  }

  .number-circle {
    font-size: 15px;
  }

  .circle {
    width: clamp(24px, 6vw, 32px);
    height: clamp(24px, 6vw, 32px);
    font-size: clamp(11px, 2.5vw, 16px);
  }

  .circle.active {
    font-size: clamp(12px, 3vw, 18px);
  }

  .step-label {
    font-size: clamp(8px, 2.3vw, 12px);
    max-width: 75%;
  }

  .stepper-line-background,
  .stepper-line-progress {
    top: 12px;
  }
}

@media (max-width: 420px) {

  .stepper-line-background,
  .stepper-line-progress {
    top: 10px;
  }
}

</style>

